import React, { useCallback, useContext } from 'react';
import { useGoodsReviewSummary } from '../../queries/useGoodsReviewSummary';
import {
  bridge,
  convertDeeplinkToWebLink,
  getWebBaseURL,
  isAndroid,
  isNil,
  localizeNumber,
} from 'shared/src/utils';
import {
  Colors,
  Divider,
  Flex,
  HorizontalSpacing,
  IconArrowRight,
  IconChevronRight,
  List,
  RemoteImage,
  Tag,
  TextButton,
  Typography,
} from '@banhala/ui-kit';
import type { ReviewSummaryItem } from 'shared/src/api';
import { useGoodsSno } from '../../hooks/useGoodsSno';
import { ABLY_INFO } from 'shared/src/constant/ablyInfo';
import { useLogClickSampleReview } from './hooks/useLogClickSampleReview';
import { EvaluationText } from 'shared/src/constant/domain/review/EvaluationText';
import { ScrollContext } from '../../context/ScrollContext';
import { useLogGoodsAnalyticsEvent } from '../../hooks/useLogGoodsAnalyticsEvent';

const IMAGE_SIZE = isAndroid ? 54 : 56;

const ReviewSummaryItemView: React.FC<{
  item: ReviewSummaryItem;
  index: number;
}> = props => {
  const { item } = props;
  const goodsSno = useGoodsSno();
  const logClickSampleReview = useLogClickSampleReview(item);

  const onPressReview = useCallback(() => {
    const path = `goods/${goodsSno}/reviews?focused_review_sno=${props.item.sno}`;
    const deeplink = `ably://webview/?url=${path}&title_bar=false`;
    logClickSampleReview();

    bridge.openDeeplink(deeplink, {
      webCallback: async () => {
        const baseURL = await getWebBaseURL();
        const convertedLink = convertDeeplinkToWebLink(deeplink);
        if (convertedLink === ABLY_INFO.url) {
          location.href = ABLY_INFO.url;
          return;
        }
        location.href = `${baseURL}/${path}`;
      },
    });
  }, [goodsSno, logClickSampleReview, props.item.sno]);

  return (
    <Flex.Row
      padding={{ horizontal: 16, vertical: 16 }}
      shadowStep={'small'}
      backgroundColor={'bg_always_white'}
      borderRadius={6}
      onPress={onPressReview}
      key={`리뷰리스트-${props.index}`}
    >
      <RemoteImage
        uri={item.images[0]}
        width={IMAGE_SIZE}
        height={IMAGE_SIZE}
        borderRadius={'small'}
      />
      <Flex flex={1} margin={{ left: 12 }} width={174} gap={4}>
        <Flex.Row alignItems={'center'} gap={4}>
          {(item.eval === 0 || item.eval === 1) && (
            <Typography type={'subtitle3'}>
              {EvaluationText[item.eval]}
            </Typography>
          )}
          {item.is_best_review && (
            <Tag color={'pink'} size={'xsmall'} type={'secondary'}>
              BEST
            </Tag>
          )}
        </Flex.Row>
        <Typography
          type={'body3'}
          color={'content_primary'}
          numberOfLines={2}
          ellipsizeMode={'tail'}
        >
          {item.contents}
        </Typography>
      </Flex>
    </Flex.Row>
  );
};

const ShowMoreReview: React.FC<{ onPressShowAll: () => void }> = props => {
  return (
    <Flex
      flex={1}
      padding={{ horizontal: 26 }}
      gap={8}
      alignItems={'center'}
      justifyContent={'center'}
      onPress={props.onPressShowAll}
      testID={'리뷰전체보기버튼2'}
      margin={{ left: 12 }}
    >
      <Flex.Center
        width={48}
        height={48}
        borderRadius={24}
        borderWidth={1}
        borderColor={'border_secondary'}
      >
        <IconArrowRight fill={Colors.content_tertiary} width={24} height={24} />
      </Flex.Center>
      <Typography color={'content_tertiary'} type={'body2'}>
        전체보기
      </Typography>
    </Flex>
  );
};

export const ReviewSummary: React.FC = () => {
  const { data: reviewSummary } = useGoodsReviewSummary();
  const { scrollToStickyView, changeContentTab } = useContext(ScrollContext);
  const logEvent = useLogGoodsAnalyticsEvent();

  const onPressShowReviewAll = useCallback(() => {
    scrollToStickyView?.();
    changeContentTab?.('REVIEW');
  }, [changeContentTab, scrollToStickyView]);

  const onPressShowImageMore = useCallback(() => {
    logEvent({ name: 'CLICK_SAMPLE_REVIEW_MORE' });
    scrollToStickyView?.();
    changeContentTab?.('REVIEW');
  }, [changeContentTab, logEvent, scrollToStickyView]);

  if (isNil(reviewSummary)) return null;

  if (reviewSummary.review_summary.length === 0) return null;

  return (
    <Flex>
      <Flex padding={{ left: 16, top: 16, right: 4, bottom: 0 }}>
        <Flex.Row alignItems={'center'}>
          <Flex gap={4} flex={1}>
            <Flex testID={`리뷰카운트-visible:true`}>
              <Typography type={'subtitle1'} color={'content_primary'}>
                리뷰{' '}
                <Typography type={'subtitle1'} color={'content_blue_primary'}>
                  {localizeNumber(reviewSummary.review.count)}
                </Typography>
              </Typography>
            </Flex>
            {reviewSummary.review.positive_percent >= 70 && (
              <Typography type={'body2'} color={'content_slate_primary'}>
                <Typography type={'subtitle2'} color={'content_slate_primary'}>
                  {reviewSummary.review.positive_percent}%
                </Typography>
                가 만족한 상품이에요
              </Typography>
            )}
          </Flex>

          <TextButton
            size={'small'}
            icon={<IconChevronRight />}
            color={'blue'}
            onPress={onPressShowReviewAll}
            testID={'리뷰전체보기버튼'}
          >
            전체보기
          </TextButton>
        </Flex.Row>
        <List<ReviewSummaryItem>
          horizontal={true}
          style={{
            marginLeft: -16,
            marginRight: -4,
          }}
          showsHorizontalScrollIndicator={false}
          data={reviewSummary.review_summary}
          contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 16 }}
          ItemSeparatorComponent={() => <HorizontalSpacing size={12} />}
          renderItem={({ item, index }) => (
            <ReviewSummaryItemView item={item} index={index} />
          )}
          ListFooterComponent={
            <ShowMoreReview onPressShowAll={onPressShowImageMore} />
          }
        />
      </Flex>
      <Divider size={'medium'} />
    </Flex>
  );
};
